import {
  Box, Typography, Paper, Stack, Button, Chip, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Grid2 as Grid, Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import UndoIcon from "@mui/icons-material/Undo";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { salesInvoicesApi, salesReturnsApi, type PriceSource } from "@/api/endpoints";
import { LinePriceExplainDialog } from "@/features/sales/LinePriceExplainDialog";
import { PayerSettlementDialog } from "@/features/sales/PayerSettlementDialog";
import { useAuth } from "@/store/auth";
import { GenericInfoButton } from "@/components/GenericInfoButton";
import { errorMessage } from "@/api/client";
import { StatusPill } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDateTime, formatDate } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/**
 * Where a line's price came from, in the reader's language. Recorded on the bill (W5) rather than
 * re-derived, so this stays true even after the rate card behind it has been revised.
 */
const PRICE_SOURCE_LABEL: Record<PriceSource, string> = {
  ServiceCharge: "Charge master rate card",
  ServiceRate: "Service rate history",
  ServiceDefault: "Service default rate",
  PriceList: "Price list",
  BatchMrp: "Batch MRP",
  ItemDefaultMrp: "Item default MRP",
  FreeText: "Entered manually",
  // W18 — a share of a fixed package price, not a rate anybody set. Saying "package" is what
  // stops the odd-looking figure reading as a charge master gone wrong.
  Package: "Share of a package price",
};

const REASON_LABEL: Record<string, string> = {
  OutOfStock: "Out of stock",
  CheaperEquivalent: "Cheaper equivalent",
  PatientPreference: "Patient preference",
  Therapeutic: "Therapeutic substitute",
  Generic: "Generic equivalent",
};

export function SalesInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const canReturn = useAuth(s => s.hasPermission("sales.return.create"));
  // Recording a claim receipt moves money and posts a journal, so it sits behind the same grant
  // as posting a bill rather than merely viewing one.
  const canPost = useAuth(s => s.hasPermission("sales.invoice.post"));

  /** W17 — which line's pricing is being explained. Null when the dialog is closed. */
  const [explainingLineId, setExplainingLineId] = useState<string | null>(null);
  /** W19 — whether the claim-settlement dialog is open. */
  const [settlingClaim, setSettlingClaim] = useState(false);

  const query = useQuery({
    queryKey: ["sales-invoice", id],
    queryFn: () => salesInvoicesApi.get(id!),
    enabled: !!id,
  });

  // Credit notes already raised against this bill (W8). Shown on the invoice itself because
  // "has any of this come back?" is the first thing anyone asks when a bill is queried.
  const returns = useQuery({
    queryKey: ["sales-returns", "for-invoice", id],
    queryFn: () => salesReturnsApi.list({ salesInvoiceId: id!, pageSize: 50 }),
    enabled: !!id,
  });

  if (query.isLoading) return <DetailSkeleton />;
  if (query.isError) return <Alert severity="error">{errorMessage(query.error)}</Alert>;
  if (!query.data) return null;
  // Backend returns the detail as a loose shape; assume the standard structure
  const si = query.data as {
    id: string; documentNo: string; documentDate: string; status: string; billType: string;
    customerId?: string; customerName?: string; customerPhone?: string;
    uhid?: string; admissionId?: string;
    subTotal: number; taxAmount: number; discountAmount: number; netAmount: number;
    /** W24 — the rounding applied to reach netAmount. Signed; zero when no rule is in force. */
    roundOff?: number;
    // W19 — one bill, two payables, settled independently.
    insuranceSchemeName?: string | null;
    coPayPct?: number | null;
    patientPayable: number; payerPayable: number;
    receivedFromPatient: number; receivedFromPayer: number;
    patientBalanceDue: number; payerBalanceDue: number;
    discountReason?: string | null;
    journalEntryId?: string; notes?: string;
    lines: Array<{
      id: string; lineNo: number; lineType: string;
      itemId?: string; itemName?: string; sku?: string;
      serviceId?: string; serviceName?: string;
      batchId?: string | null; batchNo?: string | null; expiryDate?: string | null;
      description?: string; prescriptionRef?: string;
      substitutedFromItemId?: string; substitutedFromItemName?: string; substituteReasonCode?: string;
      qty: number; rate: number; taxAmount: number; discountAmount: number; lineTotal: number;
      /** Linked generic master row; drives the monograph pop-out. */
      genericNameId?: string | null;
      // Pricing snapshot (W5). Null on lines billed before it shipped.
      listRate?: number;
      priceSource?: PriceSource | null;
      priceSourceId?: string | null;
      discountReason?: string | null;
      discountApprovedBy?: string | null;
      discountApprovedAt?: string | null;
      /** False on a line the agreement excludes — the patient's in full (W19). */
      isPayerCovered?: boolean;
      /** W22 — the promotion the server applied, and its share of the discount. */
      billingOfferId?: string | null;
      billingOfferName?: string | null;
      offerDiscountAmount?: number;
    }>;
    payments: Array<{
      id: string; mode: string; amountReceived: number; reference?: string; paidAt: string;
      paidBy?: "Patient" | "Payer";
    }>;
  };

  const totalReceived = si.payments.reduce((s, p) => s + p.amountReceived, 0);
  const balanceDue = Math.max(0, si.netAmount - totalReceived);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/sales/invoices")}>
          All invoices
        </Button>
        <Typography variant="h5" fontWeight={600}>{si.documentNo}</Typography>
        <StatusPill label={si.status} />
        <Chip label={si.billType} variant="outlined" />
        {si.journalEntryId && (
          <Tooltip title="Posted to ledger">
            <Chip icon={<ReceiptLongIcon />} label="GL posted" color="success" variant="outlined" />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {/* W8 — only a posted bill can be credited. On a draft there is nothing to return:
            no money has changed hands and no stock has left. */}
        {si.status === "Posted" && canReturn && (
          <Button variant="outlined" color="warning" startIcon={<UndoIcon />}
            onClick={() => navigate(`/sales/invoices/${si.id}/return`)}>
            Return / credit note
          </Button>
        )}
      </Stack>

      {returns.data && returns.data.items.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack spacing={0.5}>
            {returns.data.items.map(r => (
              <span key={r.id}>
                <b>{r.documentNo}</b> · {formatDate(r.documentDate)} ·{" "}
                {inr.format(r.netAmount)} refunded via {r.refundMode} · {r.reason}
              </span>
            ))}
          </Stack>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Customer</Typography>
            <Typography>
              {si.customerName ?? "—"}
              {si.customerPhone ? ` · ${si.customerPhone}` : ""}
            </Typography>
          </Grid>
          {/* Patient identity travels with the bill so ward staff can reconcile it against
              the admission without opening the customer record. */}
          {(si.uhid || si.admissionId) && (
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {si.billType === "InPatient" ? "Patient · admission" : "Patient"}
              </Typography>
              <Typography sx={{ fontFamily: "monospace" }}>
                {si.uhid ?? "—"}
                {si.admissionId ? ` · ${si.admissionId}` : ""}
              </Typography>
            </Grid>
          )}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Bill date</Typography>
            <Typography>{formatDate(si.documentDate)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Sub-total</Typography>
            <Typography>{inr.format(si.subTotal)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Tax</Typography>
            <Typography>{inr.format(si.taxAmount)}</Typography>
          </Grid>
          {/* W24 — named on the bill, because a net that does not equal subtotal + tax − discount
              is exactly what somebody queries. It posts to its own ledger head too. */}
          {(si.roundOff ?? 0) !== 0 && (
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">Round off</Typography>
              <Typography>
                {si.roundOff! > 0 ? "+" : "−"}{inr.format(Math.abs(si.roundOff!))}
              </Typography>
            </Grid>
          )}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Net</Typography>
            <Typography variant="h6" color="primary">{inr.format(si.netAmount)}</Typography>
          </Grid>
          {si.discountAmount > 0 && (
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography color="warning.main">{inr.format(si.discountAmount)}</Typography>
              {/* A bill-level giveaway is the kind that gets questioned later, so the reason it
                  was raised with sits next to the figure rather than in the audit log (W5). */}
              {si.discountReason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {si.discountReason}
                </Typography>
              )}
            </Grid>
          )}
          {si.notes && (
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography sx={{ whiteSpace: "pre-line" }}>{si.notes}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>#</TableCell>
              <TableCell sx={{ width: 100 }}>Type</TableCell>
              <TableCell>Item / Service</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Tax</TableCell>
              <TableCell align="right">Discount</TableCell>
              <TableCell align="right">Line total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {si.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>{l.lineNo}</TableCell>
                <TableCell><Chip size="small" label={l.lineType} variant="outlined" /></TableCell>
                <TableCell>
                  <Stack>
                    <strong>{l.sku ?? l.serviceName ?? "—"}</strong>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <span>{l.itemName ?? l.serviceName ?? l.description ?? ""}</span>
                      <GenericInfoButton genericNameId={l.genericNameId} />
                    </Stack>
                    {l.prescriptionRef && (
                      <Typography variant="caption" color="text.secondary">Rx ref: {l.prescriptionRef}</Typography>
                    )}
                    {/* W6 — which pack this line was priced from. When the same item appears
                        twice at different prices, this is the answer to "why". */}
                    {l.batchNo && (
                      <Typography variant="caption" color="text.secondary">
                        Batch {l.batchNo}
                        {l.expiryDate ? ` · exp ${formatDate(l.expiryDate)}` : ""}
                      </Typography>
                    )}
                    {l.substitutedFromItemId && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <SwapHorizIcon fontSize="small" color="warning" />
                        <Typography variant="caption" color="warning.main">
                          Substituted from {l.substitutedFromItemName ?? "another item"}
                          {l.substituteReasonCode ? ` — ${REASON_LABEL[l.substituteReasonCode] ?? l.substituteReasonCode}` : ""}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{l.qty}</TableCell>
                <TableCell align="right">
                  {/* W17 — click the rate to see how it got there: the decision recorded when the
                      bill was raised, and what the same question answers today. */}
                  <Tooltip title="How was this priced?">
                    <Box
                      component="button" type="button"
                      onClick={() => setExplainingLineId(l.id)}
                      sx={{
                        border: 0, background: "none", p: 0, cursor: "pointer", font: "inherit",
                        color: "inherit", textAlign: "right",
                        "&:hover .rate": { textDecoration: "underline" },
                      }}
                    >
                      <Stack alignItems="flex-end">
                        <span className="rate">{inr.format(l.rate)}</span>
                        {/* Why this price, as recorded when the bill was raised. Absent on lines
                            billed before the snapshot shipped — an honest gap, not a default. */}
                        {l.priceSource && (
                          <Typography variant="caption" color="text.secondary">
                            {PRICE_SOURCE_LABEL[l.priceSource] ?? l.priceSource}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">{inr.format(l.taxAmount)}</TableCell>
                <TableCell align="right">
                  <Stack alignItems="flex-end">
                    <span>{inr.format(l.discountAmount)}</span>
                    {/* W22 — how much of that was a promotion rather than a concession. Before
                        this the two were one number and nothing recorded which was which. */}
                    {(l.offerDiscountAmount ?? 0) > 0 && (
                      <Typography variant="caption" color="success.main">
                        {inr.format(l.offerDiscountAmount!)} · {l.billingOfferName ?? "offer"}
                      </Typography>
                    )}
                    {l.discountReason && (
                      <Tooltip title={l.discountApprovedAt
                        ? `Approved ${formatDateTime(l.discountApprovedAt)}`
                        : ""}>
                        <Typography variant="caption" color="warning.main">
                          {l.discountReason}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{inr.format(l.lineTotal)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>


      {/* W19 — an insured bill has two payables and they are chased by different people. One
          "balance due" cannot say whether the patient still owes their co-pay or the claim is
          outstanding, which is exactly the confusion that used to be resolved on paper. */}
      {si.payerPayable > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Who owes what</Typography>
            {si.insuranceSchemeName && (
              <Chip size="small" variant="outlined" label={si.insuranceSchemeName} />
            )}
            {si.coPayPct != null && si.coPayPct > 0 && (
              <Typography variant="caption" color="text.secondary">
                {si.coPayPct}% co-pay
              </Typography>
            )}
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PartyBalance
                label="Patient" payable={si.patientPayable}
                received={si.receivedFromPatient} due={si.patientBalanceDue}
                hint="co-pay plus anything the agreement does not cover"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <PartyBalance
                label={si.insuranceSchemeName ?? "Payer"} payable={si.payerPayable}
                received={si.receivedFromPayer} due={si.payerBalanceDue}
                hint="claimed, not collected at the counter"
              />
              {/* The claim only clears when somebody records the remittance — until then the
                  receivable this bill raised sits in the ledger. */}
              {si.status === "Posted" && si.payerBalanceDue > 0 && canPost && (
                <Button size="small" variant="outlined" sx={{ mt: 1 }}
                  onClick={() => setSettlingClaim(true)}>
                  Record settlement
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Payments</Typography>
        <Divider sx={{ mb: 1 }} />
        {si.payments.length === 0 && <Alert severity="info">No payments recorded yet.</Alert>}
        {si.payments.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 130 }}>From</TableCell>
                <TableCell sx={{ width: 160 }}>Mode</TableCell>
                <TableCell align="right" sx={{ width: 150 }}>Amount</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell sx={{ width: 180 }}>Paid at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {si.payments.map(p => (
                <TableRow key={p.id}>
                  <TableCell>
                    <StatusPill label={p.paidBy ?? "Patient"}
                      tone={(p.paidBy ?? "Patient") === "Payer" ? "info" : "neutral"} />
                  </TableCell>
                  <TableCell><Chip size="small" label={p.mode} variant="outlined" /></TableCell>
                  <TableCell align="right">{inr.format(p.amountReceived)}</TableCell>
                  <TableCell>{p.reference ?? "—"}</TableCell>
                  <TableCell>{p.paidAt ? formatDateTime(p.paidAt) : "—"}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell />
                <TableCell><strong>Total received</strong></TableCell>
                <TableCell align="right"><strong>{inr.format(totalReceived)}</strong></TableCell>
                <TableCell colSpan={2} />
              </TableRow>
              <TableRow>
                <TableCell />
                <TableCell><strong>Balance due</strong></TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle2" color={balanceDue > 0 ? "error" : "success.main"}>
                    {inr.format(balanceDue)}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Paper>

      <LinePriceExplainDialog
        invoiceId={si.id} lineId={explainingLineId}
        onClose={() => setExplainingLineId(null)}
      />

      <PayerSettlementDialog
        invoiceId={si.id} documentNo={si.documentNo}
        payerName={si.insuranceSchemeName ?? "the payer"}
        claimed={si.payerPayable} outstanding={si.payerBalanceDue}
        open={settlingClaim} onClose={() => setSettlingClaim(false)}
      />
    </Box>
  );
}

/**
 * One party's side of a split bill (W19): what they owe, what they have paid, what is left.
 *
 * <p>The outstanding figure leads because it is the only one anybody acts on — a claims clerk
 * chasing a TPA and a cashier chasing a co-pay are both looking for the same number about
 * different people.</p>
 */
function PartyBalance({
  label, payable, received, due, hint,
}: {
  label: string; payable: number; received: number; due: number; hint: string;
}) {
  const settled = due <= 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="subtitle2">{label}</Typography>
        {settled
          ? <Chip size="small" color="success" variant="outlined" label="settled" />
          : <Chip size="small" color="warning" variant="outlined"
              label={`${inr.format(due)} outstanding`} />}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{hint}</Typography>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">Billed</Typography>
        <Typography variant="body2">{inr.format(payable)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">Received</Typography>
        <Typography variant="body2">{inr.format(received)}</Typography>
      </Stack>
    </Box>
  );
}
